/**
 * Utilitários para autenticação e controle de acesso
 * @module auth
 */

import { supabase } from './supabase';

/**
 * Tipos de perfil de usuário
 */
export type UserRole = 'user' | 'admin_local' | 'admin_global' | 'admin';

/**
 * Verifica se o usuário atual é um administrador
 * @returns {Promise<boolean>} true se o usuário é admin, false caso contrário
 */
export async function isAdmin(): Promise<boolean> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return false;

    // Verificar se o usuário tem a role 'admin' ou 'admin_global' nos metadados
    const role = session.user.user_metadata?.role;
    return role === 'admin' || role === 'admin_global';
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
}

/**
 * Verifica se o usuário é um administrador local
 * @returns {Promise<boolean>} true se o usuário é admin local, false caso contrário
 */
export async function isLocalAdmin(): Promise<boolean> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return false;

    // Verificar se o usuário tem a role 'admin_local' nos metadados
    return session.user.user_metadata?.role === 'admin_local';
  } catch (error) {
    console.error('Error checking local admin status:', error);
    return false;
  }
}

/**
 * Obtém o papel do usuário atual
 * @returns {Promise<UserRole | null>} O papel do usuário ou null se não estiver autenticado
 */
export async function getUserRole(): Promise<UserRole | null> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return null;

    // Obter o papel do usuário dos metadados
    const role = session.user.user_metadata?.role;
    
    // Validar se é um papel válido
    if (role === 'admin' || role === 'admin_global' || role === 'admin_local' || role === 'user') {
      return role as UserRole;
    }
    
    // Se não tiver papel definido, considerar como usuário comum
    return 'user';
  } catch (error) {
    console.error('Error getting user role:', error);
    return null;
  }
}

/**
 * Promove um usuário para administrador
 * @param userId ID do usuário a ser promovido
 * @returns {Promise<boolean>} true se sucesso, false caso contrário
 */
export async function promoteToAdmin(userId: string): Promise<boolean> {
  try {
    // Verificar se o usuário atual é administrador
    if (!await isAdmin()) {
      throw new Error('Apenas administradores podem promover usuários');
    }

    const { error } = await supabase.rpc('promote_to_admin', {
      target_user_id: userId
    });

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error promoting user to admin:', error);
    return false;
  }
}

/**
 * Remove privilégios de administrador de um usuário
 * @param userId ID do usuário a ser despromovido
 * @returns {Promise<boolean>} true se sucesso, false caso contrário
 */
export async function demoteFromAdmin(userId: string): Promise<boolean> {
  try {
    // Verificar se o usuário atual é administrador
    if (!await isAdmin()) {
      throw new Error('Apenas administradores podem remover privilégios');
    }

    const { error } = await supabase.rpc('demote_from_admin', {
      target_user_id: userId
    });

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error demoting user from admin:', error);
    return false;
  }
}

/**
 * Verifica se o usuário está autenticado
 * @returns {Promise<boolean>} true se autenticado, false caso contrário
 */
export async function isAuthenticated(): Promise<boolean> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    return !!session;
  } catch (error) {
    console.error('Error checking authentication status:', error);
    return false;
  }
}

/**
 * Obtém o caminho de redirecionamento com base no papel do usuário
 * @returns {Promise<string>} Caminho para redirecionamento
 */
export async function getRedirectPathByRole(): Promise<string> {
  const role = await getUserRole();
  
  switch (role) {
    case 'admin':
    case 'admin_global':
      return '/admin-global';
    case 'admin_local':
      return '/admin-local';
    case 'user':
      return '/';
    default:
      return '/';
  }
}

/**
 * Define o papel do usuário
 * @param userId ID do usuário
 * @param role Papel a ser atribuído
 * @returns {Promise<boolean>} true se sucesso, false caso contrário
 */
export async function setUserRole(userId: string, role: UserRole): Promise<boolean> {
  try {
    // Verificar se o usuário atual é administrador
    if (!await isAdmin()) {
      throw new Error('Apenas administradores podem alterar papéis de usuários');
    }

    const { data, error } = await supabase.auth.admin.updateUserById(
      userId,
      { user_metadata: { role } }
    );

    if (error) throw error;
    return !!data.user;
  } catch (error) {
    console.error('Error setting user role:', error);
    return false;
  }
}

/**
 * Verifica se o usuário atual é um administrador global
 * @returns {Promise<boolean>} true se o usuário é admin global, false caso contrário
 */
export async function isGlobalAdmin(): Promise<boolean> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return false;

    // Verificar se o usuário tem a role 'admin_global' ou 'admin' nos metadados
    const role = session.user.user_metadata?.role;
    return role === 'admin_global' || role === 'admin';
  } catch (error) {
    console.error('Error checking global admin status:', error);
    return false;
  }
}

/**
 * Obtém a igreja do usuário atual
 * @returns {Promise<string | null>} ID da igreja ou null se não estiver definida
 */
export async function getUserChurch(): Promise<string | null> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return null;

    // Buscar o usuário no banco de dados para obter a igreja_id
    const { data, error } = await supabase
      .from('users')
      .select('igreja_id')
      .eq('id', session.user.id)
      .single();

    if (error) throw error;
    return data?.igreja_id || null;
  } catch (error) {
    console.error('Error getting user church:', error);
    return null;
  }
}

/**
 * Define a igreja do usuário
 * @param userId ID do usuário
 * @param churchId ID da igreja
 * @returns {Promise<boolean>} true se sucesso, false caso contrário
 */
export async function setUserChurch(userId: string, churchId: string): Promise<boolean> {
  try {
    // Verificar se o usuário atual é administrador
    if (!await isAdmin()) {
      throw new Error('Apenas administradores podem alterar a igreja do usuário');
    }

    const { error } = await supabase
      .from('users')
      .update({ igreja_id: churchId })
      .eq('id', userId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error setting user church:', error);
    return false;
  }
}

/**
 * Verifica se o usuário tem acesso a um conteúdo específico de uma igreja
 * @param churchId ID da igreja do conteúdo
 * @returns {Promise<boolean>} true se tem acesso, false caso contrário
 */
export async function hasChurchAccess(churchId: string): Promise<boolean> {
  try {
    // Administradores globais têm acesso a tudo
    if (await isGlobalAdmin()) return true;

    // Verificar se o usuário pertence à igreja
    const userChurch = await getUserChurch();
    return userChurch === churchId;
  } catch (error) {
    console.error('Error checking church access:', error);
    return false;
  }
}

/**
 * Registra uma ação do usuário para análise posterior
 * @param action Ação realizada
 * @param details Detalhes da ação
 * @returns {Promise<boolean>} true se sucesso, false caso contrário
 */
export async function logUserAction(action: string, details: any): Promise<boolean> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return false;

    const { error } = await supabase
      .from('user_actions_log')
      .insert([{
        user_id: session.user.id,
        action,
        details,
        ip_address: '0.0.0.0', // Em um ambiente real, você capturaria o IP
        user_agent: navigator.userAgent
      }]);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error logging user action:', error);
    return false;
  }
}